import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/api/client'
import { getApiBaseUrl } from '@/api/base-url'

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  timestamp: Date
  read: boolean
  persistent?: boolean
  duration?: number // milliseconds, default 5000
  action?: { label: string; to: string }
}

/**
 * Store для управления внутренними уведомлениями
 */
export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([])
  const loading = ref(false)

  // Computed
  const unreadCount = computed(() => notifications.value.filter((n) => !n.read).length)
  const recentNotifications = computed(() =>
    notifications.value.slice(0, 10).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()),
  )

  // Actions
  function addNotification(notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)
    const newNotification: Notification = {
      ...notification,
      id,
      timestamp: new Date(),
      read: false,
      duration: notification.duration || 5000,
    }

    notifications.value.unshift(newNotification)

    // Auto-remove non-persistent notifications after duration
    if (!notification.persistent) {
      setTimeout(() => {
        removeNotification(id)
      }, newNotification.duration)
    }

    return id
  }

  function resolveMessageArgs(
    defaultTitle: string,
    titleOrMessage: string,
    messageOrOptions?: string | Partial<Notification>,
    options?: Partial<Notification>,
  ) {
    if (typeof messageOrOptions === 'string') {
      return {
        title: titleOrMessage,
        message: messageOrOptions,
        options: options ?? {},
      }
    }

    return {
      title: defaultTitle,
      message: titleOrMessage,
      options: messageOrOptions ?? {},
    }
  }

  function showSuccess(
    titleOrMessage: string,
    messageOrOptions?: string | Partial<Notification>,
    options?: Partial<Notification>,
  ) {
    const resolved = resolveMessageArgs('Успешно', titleOrMessage, messageOrOptions, options)
    return addNotification({
      type: 'success',
      title: resolved.title,
      message: resolved.message,
      ...resolved.options,
    })
  }

  function showError(
    titleOrMessage: string,
    messageOrOptions?: string | Partial<Notification>,
    options?: Partial<Notification>,
  ) {
    const resolved = resolveMessageArgs('Ошибка', titleOrMessage, messageOrOptions, options)
    return addNotification({
      type: 'error',
      title: resolved.title,
      message: resolved.message,
      duration: 8000,
      ...resolved.options,
    })
  }

  function showWarning(
    titleOrMessage: string,
    messageOrOptions?: string | Partial<Notification>,
    options?: Partial<Notification>,
  ) {
    const resolved = resolveMessageArgs('Предупреждение', titleOrMessage, messageOrOptions, options)
    return addNotification({
      type: 'warning',
      title: resolved.title,
      message: resolved.message,
      ...resolved.options,
    })
  }

  function showInfo(
    titleOrMessage: string,
    messageOrOptions?: string | Partial<Notification>,
    options?: Partial<Notification>,
  ) {
    const resolved = resolveMessageArgs('Информация', titleOrMessage, messageOrOptions, options)
    return addNotification({
      type: 'info',
      title: resolved.title,
      message: resolved.message,
      ...resolved.options,
    })
  }

  function removeNotification(id: string) {
    const index = notifications.value.findIndex((n) => n.id === id)
    if (index !== -1) {
      notifications.value.splice(index, 1)
    }
  }

  function markAsRead(id: string) {
    const notification = notifications.value.find((n) => n.id === id)
    if (notification) {
      notification.read = true
    }
  }

  function markAllAsRead() {
    notifications.value.forEach((n) => (n.read = true))
  }

  function clearAll() {
    notifications.value = []
  }

  // WebSocket connection for real-time notifications
  let websocket: WebSocket | null = null

  function connectWebSocket() {
    if (websocket?.readyState === WebSocket.OPEN) {
      return // Already connected
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${protocol}//${window.location.host}/ws/notifications`

    try {
      websocket = new WebSocket(wsUrl)

      websocket.onopen = () => {
        console.log('WebSocket notifications connected')
      }

      websocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          handleWebSocketNotification(data)
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }

      websocket.onclose = () => {
        console.log('WebSocket notifications disconnected')
        // Attempt to reconnect after 5 seconds
        setTimeout(connectWebSocket, 5000)
      }

      websocket.onerror = (error) => {
        console.error('WebSocket error:', error)
      }
    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
      // Retry connection after 10 seconds
      setTimeout(connectWebSocket, 10000)
    }
  }

  function disconnectWebSocket() {
    if (websocket) {
      websocket.close()
      websocket = null
    }
  }

  function handleWebSocketNotification(data: {
    type?: string
    level?: Notification['type']
    title?: string
    message?: string
    persistent?: boolean
  }) {
    const notification_payload: {
      type: Notification['type']
      title: string
      message: string
      persistent?: boolean
    } = {
      type: data.level || 'info',
      title: data.title || 'Уведомление',
      message: data.message || '',
    }

    if (data.persistent !== undefined) {
      notification_payload.persistent = data.persistent
    }

    // Generic notification
    addNotification(notification_payload)
  }

  // Fetch notifications from server (for page refresh recovery)
  async function fetchNotifications() {
    loading.value = true
    try {
      const apiUrl = getApiBaseUrl()
      const response = await apiClient.get(`${apiUrl}/api/notifications`)
      if (response.data.success) {
        notifications.value = response.data.data.map(
          (n: Omit<Notification, 'timestamp'> & { timestamp: string }) => ({
            ...n,
            timestamp: new Date(n.timestamp),
          }),
        )
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    notifications,
    loading,

    // Getters
    unreadCount,
    recentNotifications,

    // Actions
    addNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeNotification,
    markAsRead,
    markAllAsRead,
    clearAll,

    // WebSocket
    connectWebSocket,
    disconnectWebSocket,

    // Server sync
    fetchNotifications,
  }
})

// Backward-compatible alias for older imports.
export const useNotificationStore = useNotificationsStore
